/**
 * Cookie utility functions for authentication
 */

const COOKIE_OPTIONS = {
  path: '/',
  sameSite: 'strict' as const,
  secure: window.location.protocol === 'https:',
};

export interface CookieOptions {
  path?: string;
  maxAge?: number; // in seconds
  expires?: Date;
  sameSite?: 'strict' | 'lax' | 'none';
  secure?: boolean;
}

/**
 * Set a cookie with the given name, value, and options
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  const opts = { ...COOKIE_OPTIONS, ...options };
  
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  
  if (opts.maxAge !== undefined) {
    cookieString += `; max-age=${opts.maxAge}`;
  }
  
  if (opts.expires) {
    cookieString += `; expires=${opts.expires.toUTCString()}`;
  }
  
  if (opts.path) {
    cookieString += `; path=${opts.path}`;
  }
  
  if (opts.sameSite) {
    cookieString += `; samesite=${opts.sameSite}`;
  }
  
  if (opts.secure) {
    cookieString += '; secure';
  }
  
  document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 */
export function getCookie(name: string): string | null {
  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');
  
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith(nameEQ)) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  
  return null;
}

/**
 * Delete a cookie by name
 */
export function deleteCookie(name: string): void {
  setCookie(name, '', {
    maxAge: -1,
    path: '/',
  });
}

/**
 * Check if a cookie exists
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}
